/**
 * TemplateCadenceTab — embedded inside a template's detail modal.
 * Lists the cadence rules bound to this template and lets the admin
 * create a new one (pre-bound to the template via lockedSource).
 *
 * Migration 114.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import {
  CadenceRule, getRules, deleteRule,
} from '../../services/cadenceService';
import CadenceRuleForm from './CadenceRuleForm';
import Modal from '../common/Modal';
import Badge from '../common/Badge';
import Button from '../common/Button';
import Skeleton from '../common/Skeleton';
import Pagination from '../common/Pagination';
import NoDataFound from '../common/NoDataFound';
import FormControl from '../common/FormControl';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmDelete, swalPermission } from '../common/confirm';
import { statusVariant } from '../../utils/statusVariant';
import extractApiError from '../../utils/apiError';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';
import './template-tabs.css';

interface Props {
  /** Registry id for the bound entity kind (SOP / RAT_TEMPLATE / CAT_TEMPLATE). */
  sourceTypeId: number;
  /** Record id in the registry-named table. */
  sourceRecordId: number;
  /** Registry type_code, passed verbatim to the form as locked context. */
  sourceTypeCode: string;
  /** Human-readable template name shown in the source chip. */
  sourceName: string;
  /** Registry display_name. */
  sourceDisplayName: string;
  /** When false, Create/Edit actions show permission swal instead of opening form. */
  canModify?: boolean;
}

/** Strip any "Paused (…)" suffix so statusVariant() sees the base lifecycle word. */
const statusBase = (status: string): string =>
  status?.startsWith('Paused') ? 'Paused' : status;

const TemplateCadenceTab: React.FC<Props> = ({
  sourceTypeId, sourceRecordId, sourceTypeCode, sourceName, sourceDisplayName, canModify = true,
}) => {
  const { formatDate } = useEntitySettings();
  const [rules, setRules] = useState<CadenceRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [editRule, setEditRule] = useState<CadenceRule | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formFooter, setFormFooter] = useState<React.ReactNode>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRules(await getRules({ source_type_id: sourceTypeId, source_record_id: sourceRecordId }));
    } catch (e: any) {
      setToast({ type: 'error', message: extractApiError(e, 'Failed to load cadence rules.') });
    } finally {
      setLoading(false);
    }
  }, [sourceTypeId, sourceRecordId]);

  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? rules.filter(r => [r.obligation_name, r.category_name, r.rule_status]
        .filter(Boolean).join(' ').toLowerCase().includes(q))
    : rules;

  // Reset to page 1 whenever the result set changes shape.
  useEffect(() => { setPage(1); }, [search, pageSize, rules.length]);

  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleNew    = () => { if (!canModify) { swalPermission(); return; } setEditRule(null); setFormFooter(null); setShowForm(true); };
  const handleEdit   = (r: CadenceRule) => { if (!canModify) { swalPermission(); return; } setEditRule(r); setFormFooter(null); setShowForm(true); };
  const handleSaved  = () => { setShowForm(false); setEditRule(null); setFormFooter(null); load(); };
  const handleCancel = () => { setShowForm(false); setEditRule(null); setFormFooter(null); };

  const handleDeleteFromForm = async (r: CadenceRule) => {
    const ok = await confirmDelete({
      title: `Delete "${r.obligation_name || `Rule #${r.id}`}"?`,
      text: 'This action cannot be undone.',
    });
    if (!ok) return;
    try {
      await deleteRule(r.id);
      setShowForm(false); setEditRule(null); setFormFooter(null); load();
      setToast({ type: 'delete', message: 'Cadence rule deleted.' });
    } catch (e: any) {
      setToast({ type: 'error', message: extractApiError(e, 'Delete failed.') });
    }
  };

  return (
    <div className="template-cadence-tab">
      <div className="template-cadence-tab-header">
        <div className="flex items-center gap-2 ml-auto">
          <FormControl.Input
            type="text"
            placeholder="Search rules…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="filter-search"
            data-testid="INP_template_cadence_search"
          />
          <Button variant="fill" icon={Plus} onClick={handleNew} className={!canModify ? 'opacity-50 cursor-not-allowed' : ''} data-testid="BTN_template_cadence_create">
            Create
          </Button>
        </div>
      </div>

      <div
        className="table-list flex-1 min-h-0"
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        {!(!loading && filtered.length === 0) && (
          <div className="table-list-scroll">
            <table className="table-list-table">
              <thead>
                <tr>
                  <th className="table-list-th table-list-cell--sno">S.No</th>
                  <th className="table-list-th">Category</th>
                  <th className="table-list-th">Obligation</th>
                  <th className="table-list-th">Handler</th>
                  <th className="table-list-th">Start</th>
                  <th className="table-list-th">End</th>
                  <th className="table-list-th table-list-cell--status">Status</th>
                </tr>
              </thead>
              <tbody aria-busy={loading} aria-live="polite">
                {loading && (
                  <Skeleton.TableRows cols={['2xs', 'sm', 'md', 'sm', 'xs', 'xs', 'pill']} />
                )}
                {!loading && paged.map((r, idx) => (
                  <tr
                    key={r.id}
                    className="table-list-row"
                    onClick={() => handleEdit(r)}
                    data-testid={`BTN_template_cadence_row_${r.id}_open`}
                  >
                    <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                    <td className="table-list-cell">
                      <span className="cad-tag">{r.category_name || r.category_id}</span>
                    </td>
                    <td className="table-list-cell">
                      <span className="table-list-cell-title">{r.obligation_name || `Rule #${r.id}`}</span>
                    </td>
                    <td className="table-list-cell">{r.fulfillment_handler || '—'}</td>
                    <td className="table-list-cell">{formatDate(r.start_date)}</td>
                    <td className="table-list-cell">
                      {r.end_date ? formatDate(r.end_date) : <span className="text-gray-400">Open-ended</span>}
                    </td>
                    <td className="table-list-cell table-list-cell--status">
                      <Badge variant={statusVariant(statusBase(r.rule_status))} size="sm">
                        {r.rule_status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div style={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
            <NoDataFound
              title={q ? 'No matching cadence rules' : 'No cadence rules bound to this template'}
              message={q ? 'No rules match the current search.' : 'Create a rule to drive rollout for this template.'}
            />
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            totalRecords={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            recordLabel="rules"
          />
        )}
      </div>

      <Modal isOpen={showForm} onClose={handleCancel} size="xl" closeOnOverlayClick={false}>
        <Modal.Header>
          {editRule
            ? <>Edit "<span className="item-highlight">{editRule.obligation_name || `Rule #${editRule.id}`}</span>"</>
            : <>Create Cadence Rule for "<span className="item-highlight">{sourceName}</span>"</>}
        </Modal.Header>
        <Modal.Body className='p-0'>
          <CadenceRuleForm
            rule={editRule ?? undefined}
            lockedSource={{
              source_type_id: sourceTypeId,
              source_record_id: sourceRecordId,
              source_type_code: sourceTypeCode,
              name: sourceName,
              display_name: sourceDisplayName,
            }}
            onSaved={handleSaved}
            onCancel={handleCancel}
            onDelete={editRule ? () => handleDeleteFromForm(editRule) : undefined}
            renderFooter={setFormFooter}
          />
        </Modal.Body>
        <Modal.Footer className="app-modal-footer--split">
          {formFooter}
        </Modal.Footer>
      </Modal>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default TemplateCadenceTab;
